import { THREE } from "../config/ThreeConfig.js";
import { isTestEnvironment } from "./runtimeEnv.js";

class Freighter {
  constructor(game, position, options = {}) {
    this.game = game;
    this.kind = "freighter";
    this.isDestroyed = false;

    this.maxHealth = Math.max(1, Number(options.maxHealth) || 140);
    this.health = this.maxHealth;
    this.speed = Math.max(0, Number(options.speed) || 18); // moves toward camera (+Z)

    this.object3D = new THREE.Group();
    this.position = position?.isVector3 ? position.clone() : new THREE.Vector3(0, 0, -700);
    this.velocity = new THREE.Vector3(0, 0, this.speed);

    this._collider = null;
    this._glow = null;
    this._age = 0;

    this._createMesh();
    this.object3D.position.copy(this.position);
  }

  _createMesh() {
    // Chunky “civilian freighter” silhouette. Keep it readable + not too shiny.
    const bodyGeom = new THREE.BoxGeometry(64, 18, 110);
    const bodyMat = new THREE.MeshPhongMaterial({
      color: 0x4a4a5a,
      emissive: 0x0a0a14,
      emissiveIntensity: 0.25,
      shininess: 20,
    });
    const body = new THREE.Mesh(bodyGeom, bodyMat);
    body.castShadow = false;
    body.receiveShadow = false;
    body.position.set(0, 0, 0);
    this.object3D.add(body);

    // “Cabin” / bridge.
    const bridgeGeom = new THREE.BoxGeometry(26, 10, 22);
    const bridgeMat = new THREE.MeshPhongMaterial({
      color: 0x303040,
      emissive: 0x121224,
      emissiveIntensity: 0.35,
      shininess: 30,
    });
    const bridge = new THREE.Mesh(bridgeGeom, bridgeMat);
    bridge.castShadow = false;
    bridge.receiveShadow = false;
    bridge.position.set(0, 8, 28);
    this.object3D.add(bridge);

    // Cargo “pods”.
    const podGeom = new THREE.BoxGeometry(18, 10, 18);
    const podMat = new THREE.MeshPhongMaterial({
      color: 0x2a2a34,
      emissive: 0x0a0a14,
      emissiveIntensity: 0.25,
      shininess: 15,
    });
    for (let i = 0; i < 4; i++) {
      const pod = new THREE.Mesh(podGeom, podMat);
      pod.castShadow = false;
      pod.receiveShadow = false;
      pod.position.set(-22 + i * 14.5, -8, -18);
      this.object3D.add(pod);
    }

    // Readability glow (subtle).
    const glowTex = this._makeGlowTexture();
    if (glowTex) {
      const glowMat = new THREE.SpriteMaterial({
        map: glowTex,
        color: 0x66ffaa,
        transparent: true,
        opacity: 0.14,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        fog: false,
        toneMapped: false,
      });
      const glow = new THREE.Sprite(glowMat);
      glow.scale.set(160, 160, 1);
      glow.position.set(0, 10, 0);
      glow.renderOrder = 2;
      glow.frustumCulled = false;
      this.object3D.add(glow);
      this._glow = glow;
    }

    // Collider (invisible).
    const colGeom = new THREE.BoxGeometry(72, 26, 118);
    const colMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false });
    const collider = new THREE.Mesh(colGeom, colMat);
    collider.name = "civilian-collider";
    collider.castShadow = false;
    collider.receiveShadow = false;
    this.object3D.add(collider);
    this._collider = collider;
  }

  _makeGlowTexture() {
    if (isTestEnvironment()) return null;
    if (typeof document === "undefined") return null;
    const canvas = document.createElement("canvas");
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0.0, "rgba(120, 255, 200, 0.55)");
    g.addColorStop(0.25, "rgba(120, 255, 200, 0.22)");
    g.addColorStop(0.7, "rgba(120, 255, 200, 0.08)");
    g.addColorStop(1.0, "rgba(120, 255, 200, 0.0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.magFilter = THREE.NearestFilter;
    tex.minFilter = THREE.NearestMipMapNearestFilter;
    tex.needsUpdate = true;
    return tex;
  }

  update(deltaTime) {
    if (this.isDestroyed) return;
    this._age += deltaTime;

    // Slight sway to keep it alive.
    const sway = Math.sin(this._age * 0.6 + this.position.z * 0.01) * 6;
    this.position.x = sway;

    this.position.add(this.velocity.clone().multiplyScalar(deltaTime));
    this.object3D.position.copy(this.position);

    if (this._glow?.material) {
      const pulse = 1 + 0.04 * Math.sin(this._age * 3.2);
      this._glow.material.opacity = 0.12 + 0.02 * (pulse - 1);
      this._glow.scale.setScalar(160 * pulse);
    }
  }

  takeDamage(amount) {
    const dmg = Math.max(0, Number(amount) || 0);
    if (dmg <= 0) return;
    this.health = Math.max(0, this.health - dmg);
    if (this.health <= 0) this.isDestroyed = true;
  }

  getBoundingBox() {
    return new THREE.Box3().setFromObject(this.object3D);
  }
}

export class FriendlyManager {
  constructor(game) {
    this.game = game;
    this.friendlies = [];
  }

  clearAll() {
    const scene = this.game?.scene;
    for (const f of this.friendlies) {
      if (scene && f?.object3D) scene.remove(f.object3D);
    }
    this.friendlies.length = 0;
  }

  spawnFreighter(position, options = {}) {
    if (isTestEnvironment()) return null;
    const f = new Freighter(this.game, position, options);
    this.friendlies.push(f);
    this.game.friendlies = this.friendlies;
    this.game?.scene?.add?.(f.object3D);
    this.game?.levelManager?.onFreighterSpawned?.(f);
    return f;
  }

  update(deltaTime) {
    if (isTestEnvironment()) return;
    const scene = this.game?.scene;
    for (let i = this.friendlies.length - 1; i >= 0; i--) {
      const f = this.friendlies[i];
      if (!f) {
        this.friendlies.splice(i, 1);
        continue;
      }
      f.update(deltaTime);

      // Despawn: if it passes the playfield, consider it “escorted out”.
      const z = Number(f.position?.z) || 0;
      const passed = z > 220;
      if (f.isDestroyed || passed) {
        if (scene && f.object3D) scene.remove(f.object3D);
        this.friendlies.splice(i, 1);
        if (f.isDestroyed) this.game?.levelManager?.onFreighterDestroyed?.(f);
        else this.game?.levelManager?.onFreighterEscorted?.(f);
      }
    }
  }
}

